import type { accountProfileSchema } from "@rosc/contracts";
import { t } from "i18next";
import { z } from "zod";
import { create } from "zustand";
import { AuthStatus } from "@/common/enums/enums";
import { clearAdminQueries, lockAdmin } from "@/lib/admin-queries";
import {
  NativeAuthRequestError,
  type NativeSession,
  nativeAuthRequest,
  nativeSessionSchema,
  nativeSocialRedirectSchema,
  renewNativeSession,
  SIGN_UP_STARTED_KEY,
  setNativeIdentity,
} from "@/lib/native-auth";
import {
  signInWithNativePasskey,
  waitForNativePasskeyAutofill,
} from "@/lib/native-passkey";
import { referenceClient } from "@/lib/orpc/client";
import { getUser } from "@/services/auth";
import type { AuthState } from "./auth.store";

let generation = 0;
// Not `generation`: refresh() bumps that, and must not drop a picked passkey.
let autofillAttempt = 0;
let renewalInterval: ReturnType<typeof setInterval> | undefined;
const stopRenewal = () => {
  clearInterval(renewalInterval);
  renewalInterval = undefined;
};
type NativeAuthState = AuthState & {
  session: NativeSession | null;
  challengeRequired: boolean;
  // A verified identity without a ROSC account yet; the user finishes sign-up.
  registrationRequired: boolean;
  // Creates the account only; the caller refreshes once sign-up is done.
  provisionAccount: (
    profile: z.infer<typeof accountProfileSchema>,
  ) => Promise<void>;
  refresh: () => Promise<void>;
  signInWithEmailOtp: (email: string, otp: string) => Promise<void>;
  signInWithPasskey: () => Promise<void>;
  signInWithPasskeyAutofill: () => Promise<void>;
  signInWithApple: () => Promise<void>;
};
const unsupported = async (): Promise<void> => {
  throw Error(
    "This account change is unavailable during authentication migration. Contact support.",
  );
};
export const useNativeAuthStore = create<NativeAuthState>((set, get) => {
  const signInWithSocial = async (provider: "google" | "apple") => {
    const version = ++generation;
    const returnURL = new URL(window.location.href);
    returnURL.searchParams.delete("error");
    returnURL.searchParams.delete("error_description");
    const result = nativeSocialRedirectSchema.parse(
      await nativeAuthRequest("sign-in/social", {
        provider,
        callbackURL: returnURL.href,
        errorCallbackURL: returnURL.href,
      }),
    );
    if (version === generation) window.location.assign(result.url);
  };
  const authenticate = async (operation: () => Promise<unknown>) => {
    const version = ++generation;
    ++autofillAttempt;
    stopRenewal();
    setNativeIdentity(null);
    set({
      user: null,
      userData: null,
      session: null,
      loading: true,
      error: null,
      challengeRequired: false,
      registrationRequired: false,
      authStatus: AuthStatus.UNAUTHENTICATED,
    });
    try {
      const result = z
        .object({ twoFactorRedirect: z.boolean().optional() })
        .parse(await operation());
      if (version !== generation) return;
      if (result.twoFactorRedirect) {
        set({
          challengeRequired: true,
          loading: false,
          authStatus: AuthStatus.UNAUTHENTICATED,
        });
        return;
      }
      set({ challengeRequired: false });
      if (version !== generation) return;
      await get().refresh();
    } catch (error) {
      if (version !== generation) return;
      set({
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : t("auth.native.errors.signInUnavailable"),
        authStatus: AuthStatus.UNAUTHENTICATED,
      });
    }
  };
  return {
    authStatus: AuthStatus.LOADING,
    user: null,
    userData: null,
    loading: false,
    error: null,
    session: null,
    challengeRequired: false,
    registrationRequired: false,
    provisionAccount: async (profile) => {
      await referenceClient.provisionMyAccount({ body: profile });
    },
    refresh: async () => {
      const version = ++generation;
      stopRenewal();
      set({ loading: true, error: null });
      try {
        const raw = await nativeAuthRequest("session");
        if (version !== generation) return;
        if (raw === null) {
          setNativeIdentity(null);
          set({
            user: null,
            userData: null,
            session: null,
            loading: false,
            registrationRequired: false,
            authStatus: AuthStatus.UNAUTHENTICATED,
          });
          return;
        }
        const session = nativeSessionSchema.parse(raw);
        const user = {
          uid: session.user.id,
          email: session.user.email,
          emailVerified: session.user.emailVerified,
          displayName: null,
          providerData: [],
        };
        const restricted =
          session.enrollmentRequired ||
          session.mfaRequired ||
          !session.user.emailVerified;
        setNativeIdentity(restricted ? null : user);
        if (restricted) {
          set({
            user: null,
            userData: null,
            session,
            loading: false,
            authStatus: AuthStatus.UNAUTHENTICATED,
          });
          return;
        }
        const { user: userData } = await getUser();
        if (version !== generation) return;
        if (!userData)
          throw Error("Your account is unavailable. Contact support.");
        set({
          user,
          userData,
          session,
          loading: false,
          registrationRequired: false,
          authStatus: AuthStatus.AUTHENTICATED,
        });
        const renew = () => {
          void renewNativeSession().catch((error: unknown) => {
            if (
              version !== generation ||
              !(error instanceof NativeAuthRequestError) ||
              error.code !== "ACCOUNT_UNAVAILABLE"
            )
              return;
            ++generation;
            stopRenewal();
            setNativeIdentity(null);
            set({
              user: null,
              userData: null,
              session: null,
              loading: false,
              challengeRequired: false,
              registrationRequired: false,
              authStatus: AuthStatus.UNAUTHENTICATED,
              error: error.message,
            });
          });
        };
        if (!session.impersonatedBy) {
          renewalInterval = setInterval(renew, 86400000);
          renew();
        }
      } catch (error) {
        if (version !== generation) return;
        if (
          error instanceof NativeAuthRequestError &&
          error.code === "ADMIN_STEP_UP_REQUIRED"
        ) {
          set({ loading: false });
          return;
        }
        setNativeIdentity(null);
        const registrationRequired =
          error instanceof NativeAuthRequestError &&
          error.code === "ACCOUNT_REQUIRED";
        if (
          registrationRequired &&
          !sessionStorage.getItem(SIGN_UP_STARTED_KEY)
        )
          sessionStorage.setItem(SIGN_UP_STARTED_KEY, String(Date.now()));
        set({
          user: null,
          userData: null,
          session: null,
          loading: false,
          registrationRequired,
          authStatus: AuthStatus.UNAUTHENTICATED,
          error: registrationRequired
            ? null
            : error instanceof Error
              ? error.message
              : t("auth.native.errors.signInUnavailable"),
        });
      }
    },
    signIn: (email, password) =>
      authenticate(() =>
        nativeAuthRequest("sign-in/email", { email, password }),
      ),
    signInWithEmailOtp: (email, otp) =>
      authenticate(() =>
        nativeAuthRequest("sign-in/email-otp", { email, otp }),
      ),
    signInWithPasskey: () => authenticate(signInWithNativePasskey),
    // Waits outside authenticate() so loading never disables the email field.
    signInWithPasskeyAutofill: async () => {
      const attempt = ++autofillAttempt;
      try {
        const result = await waitForNativePasskeyAutofill();
        if (result && attempt === autofillAttempt)
          await authenticate(async () => result);
      } catch (error) {
        if (attempt !== autofillAttempt) return;
        set({ error: error instanceof Error ? error.message : null });
        // The refused pick ended autofill; offer saved passkeys again.
        void get().signInWithPasskeyAutofill();
      }
    },
    signInWithGoogle: () => signInWithSocial("google"),
    signInWithApple: () => signInWithSocial("apple"),
    linkGoogleAccount: unsupported,
    reSignIn: async () => get().refresh(),
    signInWithCurrentUser: async () => get().refresh(),
    signOut: async () => {
      const wasAdmin = get().session?.adminStepUpExpiresIn !== undefined;
      clearAdminQueries();
      ++generation;
      ++autofillAttempt;
      stopRenewal();
      setNativeIdentity(null);
      sessionStorage.removeItem(SIGN_UP_STARTED_KEY);
      set({
        user: null,
        userData: null,
        session: null,
        challengeRequired: false,
        registrationRequired: false,
        authStatus: AuthStatus.UNAUTHENTICATED,
        loading: false,
      });
      try {
        // The server accepts "lock" for explicit revocation before sign-out.
        if (wasAdmin) await lockAdmin("lock").catch(() => {});
        await nativeAuthRequest("sign-out", {});
        set({ error: null });
      } catch {
        set({
          error: t("auth.native.errors.signOutUnconfirmed"),
        });
      }
    },
    setUser: () => {
      throw Error(
        "Native identities can only be restored from verified sessions",
      );
    },
    forgotPassword: async (email) => {
      await nativeAuthRequest("request-password-reset", {
        email,
        redirectTo: new URL("/", window.location.origin).href,
      });
    },
    getUser: async () => get().refresh(),
  };
});
